import React, { useCallback, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useTheme,
  Link,
  Tabs,
  Tab,
} from "@mui/material";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import EditIcon from "@mui/icons-material/Edit";
import LibraryAddIcon from "@mui/icons-material/LibraryAdd";
import {
  useParams,
  Navigate,
  useNavigate,
  Link as RouterLink,
} from "react-router-dom";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import EuroIcon from "@mui/icons-material/Euro";
import PercentIcon from "@mui/icons-material/Percent";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import { useSelector, useDispatch } from "react-redux";
import { useUserRole } from "../../hooks/useUserRole";
import { useBriefingMetricsSync } from "../../hooks/useBriefingMetricsSync";
import Sidebar from "../../components/sidebar/Sidebar";
import { selectSidebarCollapsed } from "../../store/slices/app/appSelector";
import { setToastMessage } from "../../store/slices/app/appSlice";
import { selectUser } from "../../store/slices/session/sessionSelector";
import {
  exportBriefingReport,
  ReportPeriod,
  BriefingReportDownloadError,
} from "../../utils/briefings/exportBriefingReport";
import {
  briefingApi,
  useGetBriefingByIdQuery,
  useGetAssociatedCampaignsQuery,
  useUpdateBriefingStatusMutation,
  useCreateBriefingVersionMutation,
} from "../../store/apis/briefingApi";
import AddAssociatedCampaignDialog from "../../components/briefings/AddAssociatedCampaignDialog";
import AssociatedCampaignsSection from "../../components/briefings/AssociatedCampaignsSection";
import BriefingMetricsSyncPanel from "../../components/briefings/BriefingMetricsSyncPanel";
import BriefingHistoryTab from "../../components/briefings/BriefingHistoryTab";
import CampaignDateRange from "../../components/briefings/CampaignDateRange";
import DeleteBriefingButton from "../../components/briefings/DeleteBriefingButton";
import DuplicateBriefingDialog from "../../components/briefings/DuplicateBriefingDialog";
import VersionEffectiveDateDialog from "../../components/briefings/VersionEffectiveDateDialog";
import ReportPeriodDialog from "../../components/briefings/ReportPeriodDialog";
import { components } from "../../types/api";
import { AssociatedCampaignWithRelations } from "../../store/apis/briefingApi";
import { BriefingRealesTable } from "../../components/briefings/BriefingRealesTable";
import {
  getAvailableStatusTransitions,
  getTransitionButtonLabel,
} from "../../utils/briefingPermissions";
import { BriefingStatusChip } from "../../components/common/StatusChips";
import PageBreadcrumbs from "../../components/layout/PageBreadcrumbs";

type BriefingPlatform = components["schemas"]["BriefingPlatform"];
const allowedViewRoles = ["ADMIN", "ACCOUNT", "TECHNICIAN", "PRICING"] as const;
// Va entre Detalles (0) e Historial (1), pero con valor propio para no mover el
// de Historial.
const PLATFORM_DATA_TAB = 2;
// Report download follows the WIP dashboard visibility: every role but CLIENT.
const reportDownloadRoles = [
  "ADMIN",
  "ACCOUNT",
  "TECHNICIAN",
  "PRICING",
  "STRATEGY",
] as const;
const ALL_BRIEFING_PLATFORMS: BriefingPlatform[] = [
  "BEAPOLO",
  "BEAPOLO_RADIO",
  "BEAPOLO_PODCAST",
  "NATADS",
  "BEAPOLO_AMAZON_PRIME",
  "BEAPOLO_TWITCH",

  "BEAPOLO_DISNEY_PLUS",
  "BEAPOLO_DOOH",
  "BEAPOLO_OOH",
  "BEAPOLO_PRENSA",
  "BEAPOLO_LG",
  "BEAPOLO_MOVISTAR",
  "BEAPOLO_NETFLIX",
  "BEAPOLO_OTT",
  "BEAPOLO_SAMSUNG_TV",
  "BEAPOLO_SPOTIFY",
  "BEAPOLO_SKIN",
  "BEAPOLO_YOUTUBE",
  "BEAPOLO_YOUTUBE_CTV",
  "BEAPOLO_REDDIT",
  "ATRESMEDIA",
  "TRES_CAT",
  "MEDIASET",
  "ADFORM",
  "ADROLL",
  "UNDETERMINED",
  "GOOGLE_ADS_SEM",
  "GOOGLE_ADS_PMAX",
  "GOOGLE_ADS_DEMANDGEN",
  "FACEBOOK",
  "INSTAGRAM",
  "FACEBOOK_AND_INSTAGRAM",
  "X_TWITTER",
  "LINKEDIN",
  "TIKTOK",
  "PINTEREST",
];

const ViewBriefing: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const { id: rawId } = useParams<{ id: string }>();
  const id = rawId?.replace(/['"/]/g, "");
  const collapsed = useSelector(selectSidebarCollapsed);
  const [activeTab, setActiveTab] = useState(0);

  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const { canEditEverything, isAccount, role } = useUserRole();

  const canView = role
    ? allowedViewRoles.includes(role as (typeof allowedViewRoles)[number])
    : false;
  const canDownloadReport = role
    ? reportDownloadRoles.includes(role as (typeof reportDownloadRoles)[number])
    : false;

  const { data, isLoading, error, refetch } = useGetBriefingByIdQuery(
    { id: id ?? "" },
    { skip: !canView || !id }
  );
  const {
    data: associatedCampaignResponse,
    isLoading: isLoadingAssociations,
    error: associatedError,
  } = useGetAssociatedCampaignsQuery(
    { id: id ?? "" },
    { skip: !canView || !id }
  );

  const [updateBriefingStatus, { isLoading: isUpdatingStatus }] =
    useUpdateBriefingStatusMutation();
  const [createBriefingVersion, { isLoading: isCreatingVersion }] =
    useCreateBriefingVersionMutation();

  const briefing = data?.data;
  const isFinalized = briefing?.status === "FINALIZED";
  const isArchived = briefing?.status === "ARCHIVED";
  const canEdit =
    (canEditEverything || isAccount) && !isFinalized && !isArchived;
  const canDuplicate = (role === "ADMIN" || role === "ACCOUNT") && !isArchived;
  const canAmend =
    (role === "ADMIN" || role === "ACCOUNT") &&
    briefing?.status !== "DRAFT" &&
    !isArchived;
  const canEditBriefingMetadata =
    (role === "ADMIN" || role === "ACCOUNT") && !isArchived;
  const isTechnician = role === "TECHNICIAN";

  const associatedCampaignsData = associatedCampaignResponse?.data as
    | AssociatedCampaignWithRelations[]
    | undefined;

  const associatedCampaigns: AssociatedCampaignWithRelations[] = useMemo(() => {
    return (
      associatedCampaignsData?.map((campaign) => ({
        ...campaign,
        goal: campaign.goal ?? null,
        target: campaign.target ?? null,
        asset: campaign.asset ?? null,
        sync: campaign.sync ?? null,
      })) ?? []
    );
  }, [associatedCampaignsData]);

  const realTotalBudget = useMemo(() => {
    return associatedCampaigns.reduce((sum, campaign) => {
      if (isTechnician) {
        return (
          sum +
          (campaign.realMetrics?.campaignInvestment ??
            campaign.goal?.totalInversion ??
            0)
        );
      } else {
        return (
          sum +
          (campaign.realMetrics?.totalInvestment ??
            campaign.goal?.totalInversion ??
            0)
        );
      }
    }, 0);
  }, [associatedCampaigns, isTechnician]);

  const [isAddDialogOpen, setAddDialogOpen] = useState(false);
  const [isDuplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [isEffectiveDateDialogOpen, setEffectiveDateDialogOpen] =
    useState(false);
  const [statusMenuAnchor, setStatusMenuAnchor] = useState<null | HTMLElement>(
    null
  );
  const [isDownloadingReport, setIsDownloadingReport] = useState(false);
  const [isReportPeriodOpen, setReportPeriodOpen] = useState(false);

  const platformOptions = ALL_BRIEFING_PLATFORMS;

  // Al terminar de actualizarse las líneas, recargar el briefing y sus líneas
  // para que los reales que se ven sean los nuevos.
  const handleMetricsRefreshed = useCallback(() => {
    if (id) {
      dispatch(briefingApi.util.invalidateTags([{ type: "Briefings", id }]));
    }
  }, [dispatch, id]);

  // En la página y no en la pestaña: el sondeo y la recarga tienen que seguir aunque se
  // cambie de pestaña, y el contador de la pestaña se ve desde cualquiera.
  const { problems: metricsProblems, inProgress: metricsInProgress } =
    useBriefingMetricsSync(id, handleMetricsRefreshed, { skip: !canView });

  if (!canView) {
    return <Navigate to="/" replace />;
  }

  const handleBack = () => {
    navigate("/briefings");
  };

  const handleEdit = () => {
    if (id) {
      navigate(`/briefings/${id}/edit`);
    }
  };

  // Descarga el report en Excel del periodo elegido: una hoja por sección
  // (totales, resumen por línea, evolución semanal y diaria y desgloses). Puede
  // tardar unos segundos si el briefing tiene muchas líneas.
  const handleDownloadReport = async (period: ReportPeriod) => {
    if (!id || !user?.token) return;
    setIsDownloadingReport(true);
    try {
      await exportBriefingReport(id, user.token, period);
      setReportPeriodOpen(false);
    } catch (err) {
      const status =
        err instanceof BriefingReportDownloadError ? err.status : undefined;
      const message =
        status === 403
          ? t(
              "briefingsView.reportErrorForbidden",
              "You don't have permission to download this report"
            )
          : status === 404
            ? t("briefingsView.reportErrorNotFound", "Briefing not found")
            : t("briefingsView.reportError", "Failed to download the report");
      dispatch(setToastMessage({ message, severity: "error" }));
    } finally {
      setIsDownloadingReport(false);
    }
  };

  const handleStatusChange = async (
    newStatus: components["schemas"]["BriefingStatus"],
    effectiveDate?: string
  ) => {
    if (!id) return;

    if (
      newStatus === "FINALIZED" &&
      briefing &&
      briefing.baseBriefingId &&
      !effectiveDate
    ) {
      setEffectiveDateDialogOpen(true);
      return;
    }

    try {
      await updateBriefingStatus({
        id,
        status: newStatus,
        effectiveDate,
      }).unwrap();
      // Status will update automatically via cache invalidation
    } catch (err) {
      console.error("Failed to update briefing status:", err);
      // Error handling - could add a toast notification here
    }
  };

  const handleCreateVersion = async () => {
    if (!id) return;
    try {
      const response = await createBriefingVersion({ id }).unwrap();
      if (response.data?.id) {
        navigate(`/briefings/${response.data.id}`);
      }
    } catch (err) {
      console.error("Failed to create new version:", err);
    }
  };

  const renderStatusChip = () => {
    if (!briefing) return null;
    return <BriefingStatusChip status={briefing.status} />;
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <Stack direction="row" justifyContent="center" py={8}>
          <CircularProgress />
        </Stack>
      );
    }

    if (error) {
      return (
        <Alert severity="error">
          {t("briefingsView.error", "Unable to load briefing")}
        </Alert>
      );
    }

    if (!briefing) {
      return (
        <Alert severity="warning">
          {t("briefingsView.notFound", "Briefing not found")}
        </Alert>
      );
    }

    return (
      <Stack spacing={3}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            aria-label="briefing tabs"
          >
            <Tab
              value={0}
              label={t("briefingsView.tabs.details", "Detalles & Campañas")}
            />
            <Tab
              value={PLATFORM_DATA_TAB}
              label={
                <Stack direction="row" spacing={1} alignItems="center">
                  <span>
                    {t("briefingsView.tabs.platformData", "Platform data")}
                  </span>
                  {metricsInProgress > 0 ? (
                    <CircularProgress size={14} />
                  ) : (
                    metricsProblems > 0 && (
                      <Chip
                        size="small"
                        color="warning"
                        label={metricsProblems}
                        sx={{ height: 20 }}
                      />
                    )
                  )}
                </Stack>
              }
            />
            <Tab
              value={1}
              label={t("briefingsView.tabs.history", "Historial")}
            />
          </Tabs>
        </Box>

        {activeTab === 0 && (
          <Stack spacing={3}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                border: `1px solid ${theme.palette.grey[200]}`,
                p: { xs: 2, md: 4 },
              }}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                mb={3}
              >
                <Typography variant="h6" fontWeight={700}>
                  {t("briefingsView.briefingDetails", "Briefing details")}
                </Typography>
                {canEditBriefingMetadata && (
                  <Tooltip title={t("common.edit", "Edit")}>
                    <span>
                      <IconButton
                        onClick={handleEdit}
                        size="small"
                        color="primary"
                        sx={{ bgcolor: "action.hover" }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </Stack>

              {/* HIGHLIGHT KPI CARDS */}
              <Grid container spacing={2} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      bgcolor: theme.palette.primary.light + "11",
                      border: `1px solid ${theme.palette.primary.light}44`,
                      borderRadius: 2,
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                      transition: "box-shadow 0.2s",
                      "&:hover": { boxShadow: theme.shadows[2] },
                    }}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      color="primary.main"
                    >
                      <AccountBalanceWalletIcon fontSize="small" />
                      <Typography variant="subtitle2" fontWeight={600}>
                        {t("briefingsCreate.form.totalBudget", "Total budget")}
                      </Typography>
                    </Stack>
                    <Typography variant="h5" fontWeight={700}>
                      {(briefing.totalBudget ?? 0).toLocaleString()}{" "}
                      <EuroIcon
                        fontSize="small"
                        sx={{ verticalAlign: "middle", mb: "2px" }}
                      />
                    </Typography>
                    {associatedCampaigns.length > 0 && (
                      <Chip
                        size="small"
                        label={`${t("briefingsView.realTotal", "Real")}: ${realTotalBudget.toLocaleString()}`}
                        color={
                          Math.abs(
                            realTotalBudget - (briefing.totalBudget ?? 0)
                          ) < 0.01
                            ? "success"
                            : realTotalBudget > (briefing.totalBudget ?? 0)
                              ? "error"
                              : "warning"
                        }
                        sx={{ mt: 1, alignSelf: "flex-start" }}
                      />
                    )}
                  </Paper>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      bgcolor: theme.palette.secondary.light + "11",
                      border: `1px solid ${theme.palette.secondary.light}44`,
                      borderRadius: 2,
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                      transition: "box-shadow 0.2s",
                      "&:hover": { boxShadow: theme.shadows[2] },
                    }}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      color="secondary.main"
                    >
                      <PercentIcon fontSize="small" />
                      <Typography variant="subtitle2" fontWeight={600}>
                        {t("briefingsCreate.form.fee", "Fee")}
                      </Typography>
                    </Stack>
                    <Typography variant="h5" fontWeight={700}>
                      {new Intl.NumberFormat(undefined, {
                        style: "percent",
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 2,
                      }).format((briefing.fee ?? 0) / 100)}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mt: 1 }}
                    >
                      Total+Fee:{" "}
                      {new Intl.NumberFormat("es-ES", {
                        style: "currency",
                        currency: "EUR",
                      }).format(
                        (briefing.totalBudget ?? 0) *
                          (1 + (briefing.fee ?? 0) / 100)
                      )}
                    </Typography>
                  </Paper>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      bgcolor: theme.palette.info.light + "11",
                      border: `1px solid ${theme.palette.info.light}44`,
                      borderRadius: 2,
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                      transition: "box-shadow 0.2s",
                      "&:hover": { boxShadow: theme.shadows[2] },
                    }}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      color="info.main"
                    >
                      <CalendarMonthIcon fontSize="small" />
                      <Typography variant="subtitle2" fontWeight={600}>
                        {t("briefingsView.period", "Period")}
                      </Typography>
                    </Stack>
                    <CampaignDateRange
                      startDate={briefing.campaignsStartDate}
                      endDate={briefing.campaignsEndDate}
                      month={briefing.month}
                      year={briefing.year}
                      variant="h5"
                    />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mt: 1 }}
                    >
                      Update:{" "}
                      {briefing.updatedAt
                        ? dayjs(briefing.updatedAt).format("YYYY-MM-DD")
                        : "-"}
                    </Typography>
                  </Paper>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      bgcolor: theme.palette.background.default,
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 2,
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                      alignItems: "flex-start",
                      transition: "box-shadow 0.2s",
                      "&:hover": { boxShadow: theme.shadows[2] },
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      fontWeight={600}
                    >
                      {t("briefings.table.status", "Status")}
                    </Typography>
                    <Box
                      sx={{
                        mt: "auto",
                        pt: 1,
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                      }}
                    >
                      {renderStatusChip()}
                      {briefing.version !== undefined &&
                        briefing.version > 1 && (
                          <Chip
                            label={`v${briefing.version}`}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        )}
                    </Box>
                  </Paper>
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                {t("briefingsView.briefingInformation", "Briefing Information")}
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t("briefings.table.name", "Briefing Name")}
                  </Typography>
                  <Typography variant="h6" fontWeight={700} sx={{ mt: 0.5 }}>
                    {briefing.name}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t("briefings.table.company", "Company")}
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5 }}>
                    {briefing.companyName ?? briefing.companyId}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t("briefingsCreate.form.briefingType")}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ mt: 0.5, wordBreak: "break-word" }}
                  >
                    {t(
                      `briefingsCreate.form.briefingTypeOptions.${briefing.briefingType}`
                    )}
                  </Typography>
                </Grid>

                {briefing.baseBriefingId && (
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {t("briefingsView.originalBriefing", "Original Briefing")}
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 0.5 }}>
                      <Link
                        component={RouterLink}
                        to={`/briefings/${briefing.baseBriefingId}`}
                        sx={{
                          textDecoration: "underline",
                          textUnderlineOffset: "4px",
                          color: "primary.main",
                          fontWeight: 600,
                          "&:hover": { color: "primary.dark" },
                        }}
                      >
                        {t("briefingsView.viewOriginal", "View Original")}
                      </Link>
                    </Typography>
                  </Grid>
                )}
              </Grid>

              <Divider sx={{ my: 3 }} />

              <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                {t("briefingsView.campaignDetails", "Campaign details")}
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t("briefingsCreate.form.campaignName", "Campaign Name")}
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5 }}>
                    {briefing.campaignName ?? t("common.notAvailable", "N/A")}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t("briefingsCreate.form.brandOrProduct", "Brand/Product")}
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5 }}>
                    {briefing.brandOrProduct ?? t("common.notAvailable", "N/A")}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t("briefingsCreate.form.notes", "Notes")}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ mt: 0.5, whiteSpace: "pre-wrap" }}
                  >
                    {briefing.notes || "-"}
                  </Typography>
                </Grid>

                {briefing.imageUrl && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {t("briefingsView.image", "Briefing Image")}
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                      <img
                        src={briefing.imageUrl || ""}
                        alt="Briefing attachment"
                        style={{
                          height: 200,
                          width: "auto",
                          borderRadius: 8,
                          objectFit: "cover",
                          boxShadow: theme.shadows[1],
                        }}
                      />
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Paper>

            <AssociatedCampaignsSection
              campaigns={associatedCampaigns}
              isLoading={isLoadingAssociations}
              hasError={Boolean(associatedError)}
              briefingId={id ?? ""}
              onCampaignsChange={() => {
                refetch();
              }}
              onAddCampaign={canEdit ? () => setAddDialogOpen(true) : undefined}
              isEditMode={canEdit}
              briefingFee={briefing.fee ?? 0}
              briefingBudget={briefing.totalBudget ?? 0}
              briefingStatus={briefing.status}
              briefing={briefing}
              platformOptions={platformOptions}
            />

            {role && ["ADMIN", "PRICING", "TECHNICIAN"].includes(role) && (
              <BriefingRealesTable briefing={briefing} />
            )}
          </Stack>
        )}

        {activeTab === PLATFORM_DATA_TAB && id && (
          <BriefingMetricsSyncPanel briefingId={id} />
        )}

        {activeTab === 1 && id && <BriefingHistoryTab briefingId={id} />}
      </Stack>
    );
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <Sidebar />
      <Box
        sx={{
          flexGrow: 1,
          minWidth: 0,
          px: { xs: 2, md: 4 },
          py: { xs: 2, md: 3 },
          transition: "margin-left 0.3s ease",
          ...(collapsed
            ? { ml: `calc(${theme.spacing(7)} + 12px)` }
            : { ml: `calc(${theme.spacing(32)} + 12px)` }),
          display: "flex",
          flexDirection: "column",
          gap: 3,
          "@media print": {
            ml: 0,
            px: 0,
            py: 0,
            width: "100%",
            maxWidth: "100%",
          },
        }}
      >
        <PageBreadcrumbs />

        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            border: `1px solid ${theme.palette.grey[200]}`,
            p: { xs: 2, md: 3 },
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
          >
            <Button
              startIcon={<KeyboardBackspaceIcon />}
              onClick={handleBack}
              variant="text"
              sx={{ "@media print": { display: "none" } }}
            >
              {t("briefingsCreate.back", "Back")}
            </Button>
            <Typography
              variant="h5"
              fontWeight={700}
              sx={{ fontFamily: "Montserrat" }}
            >
              {t("briefingsView.title", "Briefing details")}
            </Typography>
            {/* Status chip in the header bar */}
            {briefing && renderStatusChip()}
          </Stack>
          {briefing && (
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              flexWrap="wrap"
              useFlexGap
              sx={{
                alignSelf: { xs: "stretch", md: "center" },
                "@media print": { display: "none" },
              }}
            >
              <Tooltip title={t("briefingsView.downloadPdf", "Download PDF")}>
                <IconButton
                  color="primary"
                  onClick={() => window.print()}
                  sx={{ bgcolor: "action.hover" }}
                >
                  <PictureAsPdfIcon />
                </IconButton>
              </Tooltip>

              {canDownloadReport && (
                <Button
                  variant="outlined"
                  color="primary"
                  disabled={isDownloadingReport}
                  onClick={() => setReportPeriodOpen(true)}
                  startIcon={
                    isDownloadingReport ? (
                      <CircularProgress size={18} />
                    ) : (
                      <FileDownloadIcon />
                    )
                  }
                  sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
                >
                  {t("briefingsView.downloadReport", "Download report")}
                </Button>
              )}

              {canDuplicate && (
                <Tooltip title={t("common.duplicate", "Duplicate")}>
                  <IconButton
                    color="primary"
                    onClick={() => setDuplicateDialogOpen(true)}
                    sx={{ bgcolor: "action.hover" }}
                  >
                    <LibraryAddIcon />
                  </IconButton>
                </Tooltip>
              )}

              {canAmend && (
                <Button
                  variant="contained"
                  color="secondary"
                  disabled={isCreatingVersion}
                  onClick={handleCreateVersion}
                  sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
                >
                  {isCreatingVersion ? (
                    <CircularProgress size={24} />
                  ) : (
                    t("briefingsView.amend", "Amend (New Version)")
                  )}
                </Button>
              )}

              {/* Status Transition Dropdown */}
              {role &&
                getAvailableStatusTransitions(
                  role,
                  briefing.status,
                  briefing.briefingType
                ).length > 0 && (
                  <>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={(e) => setStatusMenuAnchor(e.currentTarget)}
                      disabled={
                        isUpdatingStatus || isCreatingVersion || isArchived
                      }
                      sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
                    >
                      {t("briefingsView.changeStatus", "Change Status")}
                    </Button>
                    <Menu
                      anchorEl={statusMenuAnchor}
                      open={Boolean(statusMenuAnchor)}
                      onClose={() => setStatusMenuAnchor(null)}
                    >
                      {getAvailableStatusTransitions(
                        role,
                        briefing.status,
                        briefing.briefingType
                      ).map((newStatus) => (
                        <MenuItem
                          key={newStatus}
                          onClick={() => {
                            setStatusMenuAnchor(null);
                            handleStatusChange(newStatus);
                          }}
                        >
                          {getTransitionButtonLabel(briefing.status, newStatus)}
                        </MenuItem>
                      ))}
                    </Menu>
                  </>
                )}

              {canEdit && canEditBriefingMetadata && (
                <DeleteBriefingButton
                  briefingId={briefing.id}
                  onDeleted={handleBack}
                />
              )}
            </Stack>
          )}
        </Paper>

        {renderContent()}
      </Box>
      {briefing && id && (
        <>
          <AddAssociatedCampaignDialog
            open={isAddDialogOpen}
            onClose={() => setAddDialogOpen(false)}
            briefingId={id}
            briefing={briefing}
            platformOptions={platformOptions}
            onSuccess={() => {
              setAddDialogOpen(false);
              refetch();
            }}
          />
          <DuplicateBriefingDialog
            open={isDuplicateDialogOpen}
            onClose={() => setDuplicateDialogOpen(false)}
            briefingId={id}
          />
          <VersionEffectiveDateDialog
            open={isEffectiveDateDialogOpen}
            onClose={() => setEffectiveDateDialogOpen(false)}
            isLoading={isUpdatingStatus}
            onConfirm={(date) => {
              setEffectiveDateDialogOpen(false);
              handleStatusChange("FINALIZED", date.toISOString());
            }}
          />
          <ReportPeriodDialog
            open={isReportPeriodOpen}
            onClose={() => setReportPeriodOpen(false)}
            onDownload={handleDownloadReport}
            isLoading={isDownloadingReport}
          />
        </>
      )}
    </Box>
  );
};

export default ViewBriefing;
